/**
 * t374u: the company's branding at runtime (docs/branding.md).
 *
 * Before sign-in the company comes from the address (GET /branding?host=); after sign-in from the current workspace
 * (/me carries each workspace's branding). The theme is the `--brand-*` CSS variables from deriveBrandTheme, injected
 * as `:root:root{...}` so it beats index.css's defaults (The Hub's). The last branding is cached in localStorage and
 * an inline script in index.html applies it before the first paint, so a company never flashes The Hub's look.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandingApi, brandThemeCss, DEFAULT_BRANDING, deriveBrandTheme, HUB_COLORS, isPlatformHost, PRODUCT_NAME, type WorkspaceBranding } from '@tradeoutlet/shared';
import { api } from './api';
import { useAuth } from './auth';
import { env } from './env';
import { useWorkspace } from './hooks';

/** localStorage key; index.html's boot script reads the same key and fields. */
export const BRAND_CACHE_KEY = 'hub.brand.v1';
export const BRAND_STYLE_ID = 'hub-brand-theme';

/** The Hub's built-in artwork (apps/web/public/hub). */
export const HUB_ASSETS = {
  logoOnDark: '/hub/lockup-dark.svg', logoOnLight: '/hub/lockup-light.svg', markOnDark: '/hub/mark-dark.svg', mark: '/hub/mark.svg',
  favicon: '/hub/favicon.svg', favicon32: '/hub/favicon-32.png', appleTouch: '/hub/apple-touch-icon.png', manifest: '/site.webmanifest',
} as const;

/** What the web app shows, flattened (and what the cache holds). URLs are absolute or root-relative. */
export interface Brand {
  version: string;
  custom: boolean;
  /** "Trade Outlet - The Hub", or "The Hub". */
  title: string;
  company: string | null;
  /** Short name (headers, the phone tab title); the company name, or The Hub. */
  short: string;
  /** Legal entity for the sign-in footer ("Trade Outlet Ltd"), or null. */
  legal: string | null;
  /** Logo for dark surfaces (the rail, sign-in), logo for light surfaces, and the square mark (collapsed rail). */
  logoOnDark: string | null;
  logoOnLight: string | null;
  mark: string | null;
  favicon: string;
  appleTouch: string;
  manifest: string;
  themeColor: string;
  primary: string;
  accent: string;
  css: string;
}

const abs = (path: string | null | undefined) => (path ? `${env.apiUrl.replace(/\/$/, '')}${path}` : null);

export function brandFrom(b: WorkspaceBranding, host = typeof location !== 'undefined' ? location.host : ''): Brand {
  const theme = deriveBrandTheme({ primary: b.primary_color, accent: b.accent_color }, HUB_COLORS);
  const a = b.assets;
  const logoLight = abs(a.logo_light), logoDark = abs(a.logo_dark), mark = abs(a.logo_mark), favicon = abs(a.favicon), icon = abs(a.app_icon);
  return {
    version: b.version, custom: b.custom, title: b.app_title, company: b.company_name,
    short: b.short_name ?? b.company_name ?? PRODUCT_NAME, legal: b.legal_name,
    // A company with one logo uses it everywhere (Trade Outlet's yellow box reads on black and on white).
    logoOnDark: b.custom ? logoDark ?? logoLight : HUB_ASSETS.logoOnDark,
    logoOnLight: b.custom ? logoLight ?? logoDark : HUB_ASSETS.logoOnLight,
    mark: b.custom ? mark : HUB_ASSETS.markOnDark,
    favicon: favicon ?? icon ?? HUB_ASSETS.favicon,
    appleTouch: icon ?? favicon ?? HUB_ASSETS.appleTouch,
    manifest: b.custom ? `${env.apiUrl.replace(/\/$/, '')}/branding/manifest.webmanifest?host=${encodeURIComponent(host)}` : HUB_ASSETS.manifest,
    themeColor: theme.primary, primary: theme.primary, accent: theme.accent,
    css: brandThemeCss(theme),
  };
}

export const HUB_BRAND: Brand = brandFrom(DEFAULT_BRANDING);

function readCache(): Brand | null {
  try {
    const v = JSON.parse(localStorage.getItem(BRAND_CACHE_KEY) ?? 'null') as Brand | null;
    return v && typeof v.css === 'string' && typeof v.title === 'string' ? v : null;
  } catch { return null; }
}

function setLink(rel: string, href: string, extra: Record<string, string> = {}) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]${extra.type ? `[type="${extra.type}"]` : ''}`);
  if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
  for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
  if (el.getAttribute('href') !== href) el.href = href;
}

/** Puts a brand on the page: theme variables, tab title, favicons, theme colour and the web app manifest. */
export function applyBrand(b: Brand) {
  let style = document.getElementById(BRAND_STYLE_ID) as HTMLStyleElement | null;
  if (!style) { style = document.createElement('style'); style.id = BRAND_STYLE_ID; document.head.appendChild(style); }
  if (style.textContent !== b.css) style.textContent = b.css;
  setLink('icon', b.favicon, { type: b.favicon.endsWith('.png') ? 'image/png' : 'image/svg+xml' });
  // The 32px PNG fallback only exists for The Hub's artwork; a company's favicon replaces it.
  const png = document.head.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (png) png.href = b.custom ? b.favicon : HUB_ASSETS.favicon32;
  setLink('apple-touch-icon', b.appleTouch);
  setLink('manifest', b.manifest);
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) { meta = document.createElement('meta'); meta.name = 'theme-color'; document.head.appendChild(meta); }
  meta.content = b.themeColor;
  // Signed-out pages have no top bar to set it; signed in, the top bar adds the unread count on top.
  if (document.title.replace(/^\(\d+\+?\) /, '') !== b.title) document.title = b.title;
}

// A tiny store so components re-render when the brand changes.
let current: Brand = (typeof localStorage !== 'undefined' && readCache()) || HUB_BRAND;
const listeners = new Set<() => void>();
export function setBrand(b: Brand) {
  if (b.version === current.version && b.css === current.css && b.manifest === current.manifest) return;
  current = b;
  try { localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(b)); } catch { /* private mode */ }
  applyBrand(b);
  listeners.forEach(l => l());
}
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export const useBrand = () => useSyncExternalStore(subscribe, () => current, () => current);

/**
 * Keeps the page in the right company's colours: the address's company before sign-in, the current workspace's
 * after. Render once, inside the auth and query providers.
 */
export function BrandingSync() {
  const { status } = useAuth();
  const ws = useWorkspace();
  const signedIn = status === 'signed_in';
  const pub = useQuery({
    queryKey: ['branding', 'public', location.host],
    queryFn: () => brandingApi(api).forHost(location.host),
    enabled: !signedIn || (ws !== null && !ws.branding),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const b = signedIn && ws?.branding ? ws.branding : pub.data;
  useEffect(() => { applyBrand(current); }, []);
  // t401u: signed out on The Hub's own address (app.thehubapp.co.uk), drop the last company's look straight away
  // (and from the cache) instead of waiting for the public answer, which is neutral there anyway.
  useEffect(() => { if (status === 'signed_out' && isPlatformHost(location.host)) setBrand(HUB_BRAND); }, [status]);
  useEffect(() => { if (b) setBrand(brandFrom(b)); }, [b]);
  return null;
}
